import type {
  EventTypeView,
  MarketId,
  MeetingBasedMarketView,
  MeetingId,
  MeetingView,
  RaceId,
  RaceView
} from '@ps/isp-web-services-types';
import type { Selector } from 'react-redux';

import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  RootState,
  SelectorConfig,
  SelectorCreator,
  Slicer,
  WidgetModelConfig
} from '@swf/core/store';
import type { SportsWidgetConfig } from '@swf/sports/pages/types';
import type { EventTypeId } from '@swf/sports/types/eventType';

import type { MeetingBasedMarketTemplatesView } from '../../types/templates';

export type EventTypesSelector = Selector<RootState, MeetingBasedMarketTemplatesView['eventTypes']>;
export type MarketsSelector = Selector<RootState, MeetingBasedMarketTemplatesView['markets']>;
export type MeetingsSelector = Selector<RootState, MeetingBasedMarketTemplatesView['meetings']>;
export type RacesSelector = Selector<RootState, MeetingBasedMarketTemplatesView['races']>;

const initialState: MeetingBasedMarketTemplatesView = {
  eventTypes: {},
  markets: {},
  meetings: {},
  races: {}
};

export const selectors = (
  slice: Slicer<MeetingBasedMarketTemplatesView>,
  createSelector: SelectorCreator
) => ({
  meetings: (): MeetingsSelector => slice((state) => state.meetings),
  meeting(this: { meetings: MeetingsSelector }) {
    return createSelector(
      this.meetings,
      (_state: RootState, meetingId: MeetingId) => meetingId,
      (meetings, meetingId): MeetingView => meetings[meetingId]
    );
  },
  races: (): RacesSelector => slice((state) => state.races),
  race(this: { races: RacesSelector }) {
    return createSelector(
      this.races,
      (_state: RootState, raceId: RaceId) => raceId,
      (races, raceId): RaceView => races[raceId]
    );
  },
  eventTypes: (): EventTypesSelector => slice((state) => state.eventTypes),
  eventType(this: { eventTypes: EventTypesSelector }) {
    return createSelector(
      this.eventTypes,
      (_state: RootState, eventTypeId: EventTypeId) => eventTypeId,
      (eventTypes, eventTypeId): EventTypeView => eventTypes[eventTypeId] as EventTypeView
    );
  },
  markets: (): MarketsSelector => slice((state) => state.markets),
  market(this: { markets: MarketsSelector }) {
    return createSelector(
      this.markets,
      (_state: RootState, marketId: MarketId) => marketId,
      (markets, marketId): MeetingBasedMarketView | undefined => markets[marketId]
    );
  },
  marketIds(this: { markets: MarketsSelector }) {
    return createSelector(this.markets, (markets) => Object.keys(markets));
  },
  marketTemplateData(this: {
    meetings: MeetingsSelector;
    races: RacesSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.meetings,
      this.races,
      this.eventTypes,
      this.markets,
      (meetings, races, eventTypes, markets): MeetingBasedMarketTemplatesView => ({
        meetings,
        races,
        eventTypes,
        markets
      })
    );
  },
  /**
   * @deprecated GSFE-2137 - use "marketTemplateData" instead
   */
  getMarketTemplateData(this: {
    meetings: MeetingsSelector;
    races: RacesSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.meetings,
      this.races,
      this.eventTypes,
      this.markets,
      (meetings, races, eventTypes, markets): MeetingBasedMarketTemplatesView => ({
        meetings,
        races,
        eventTypes,
        markets
      })
    );
  }
});

export const withMeetingBasedMarketTemplateModelState = <T>(
  modelState: T
): T & MeetingBasedMarketTemplatesView => ({ ...initialState, ...modelState });

export function withMeetingBasedMarketTemplateModelSelectors<
  S extends MeetingBasedMarketTemplatesView,
  F extends (slice: Slicer<S>, createSelector: SelectorCreator) => SelectorConfig<S>
>(_state: S, modelSelectorFactory: F) {
  return (slice: Slicer<S>, createSelector: SelectorCreator) =>
    ({
      ...selectors(slice, createSelector),
      ...modelSelectorFactory(slice, createSelector)
    } as ReturnType<typeof selectors> & ReturnType<F>);
}

export interface MeetingMarketTemplatesModel
  extends WidgetModelConfig<SportsWidgetConfig, MeetingBasedMarketTemplatesView> {
  selectors: typeof selectors;
}

// @ts-ignore fix type or remove use widget?
export const useMeetingMarketTemplatesWidget: UseWidget<void, MeetingMarketTemplatesModel> =
  useWidget;
